import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface A4PreviewProps {
    children: ReactNode
    className?: string
}

const SHEET_WIDTH = 793.7

const SHEET_HEIGHT = 1122.5

const A4Preview: FC<A4PreviewProps> = ({ children, className }): ReactNode => {
    const outerRef = useRef<HTMLDivElement>(null)
    const innerRef = useRef<HTMLDivElement>(null)
    const [scale, setScale] = useState(1)
    const [height, setHeight] = useState(SHEET_HEIGHT)

    useEffect(() => {
        const outer = outerRef.current
        const inner = innerRef.current
        if (!outer || !inner) return

        const update = () => {
            setScale(Math.min(1, outer.clientWidth / SHEET_WIDTH))
            setHeight(inner.offsetHeight || SHEET_HEIGHT)
        }

        update()

        const observer = new ResizeObserver(update)
        observer.observe(outer)
        observer.observe(inner)

        return () => observer.disconnect()
    }, [])

    return (
        <div ref={outerRef} className={cn('w-full', className)}>
            <div
                className='relative mx-auto overflow-hidden rounded-sm bg-white shadow-[0_1px_2px_rgba(15,23,42,0.06),0_12px_32px_-14px_rgba(15,23,42,0.28)] ring-1 ring-black/5'
                style={{ width: SHEET_WIDTH * scale, height: height * scale }}
            >
                <div
                    ref={innerRef}
                    className='absolute top-0 left-0 origin-top-left'
                    style={{ width: SHEET_WIDTH, transform: `scale(${scale})` }}
                >
                    {children}
                </div>
            </div>
        </div>
    )
}

export default A4Preview
